import { useState, useEffect, useRef } from 'react';
import { useNavigate } from 'react-router-dom';
import { useQuery } from '@tanstack/react-query';

import useAuth from '../../auth/useAuth';
import { useSoundStatus } from '../../context/SoundStatusContext';

import {
  scenarioLevelsQuery,
  scoresStatsQuery,
  generalInfoQuery
} from './api';

import BadgeMaster from './images/badge-master.png';
import BadgeWarrior from './images/badge-warrior.png';
import BadgeNewbie from './images/badge-newbie.png';
import BadgeNoob from './images/badge-noob.png';

import BadgeMasterSound from './sounds/badge-master.mp3';
import BadgeWarriorSound from './sounds/badge-warrior.mp3';
import BadgeNewbieSound from './sounds/badge-newbie.mp3';
import BadgeNoobSound from './sounds/badge-noob.mp3';

import { fadeOutSound } from '../../utils';

import Layout from '../UI/introGameLayout/layout';


import './styles.css';

export default function GameBadge() {
  const navigate = useNavigate();

  const { getGuidedPlay, getCurrentClassificationGameId, getUser } = useAuth();

  const user = getUser();

  const shouldGuidedPagesBeDisplayed = getGuidedPlay();

  const { soundStatus } = useSoundStatus();
  const currentSoundEffect = useRef(null);

  const masterEffect = useRef(new Audio(BadgeMasterSound));
  const warriorEffect = useRef(new Audio(BadgeWarriorSound));
  const newbieEffect = useRef(new Audio(BadgeNewbieSound));
  const noobEffect = useRef(new Audio(BadgeNoobSound));

  const [score, setScore] = useState(0);
  const [badge, setBadge] = useState('');

  const gameId = getCurrentClassificationGameId();
  const [scenarioId, setScenarioId] = useState(0);
  
  const { data: generalInfo } = useQuery(generalInfoQuery()) || {};
  const games = generalInfo?.games;
  const gamesSessions = generalInfo?.sessions;

  // For tournament games, skip this page and go directly to summary
  useEffect(() => {
    if (!games) return;
    const currentGame = games.find(game => game.id === gameId);
    if (currentGame?.tournament_ids?.length > 0) {
      navigate('/classification/summary', { replace: true });
    }
  }, [games, gameId, navigate]);

  useEffect(() => {
    if(games) {
      const game = games.find(game => game.game_type.includes('Account Classification') && game.id === gameId);

      if(game && game.scenario) {
        setScenarioId(game.scenario);
      }
    }
  }, [games, gameId]);

  const [currentGameSessionAttempt, setCurrentGameSessionAttempt] = useState(0);

  useEffect(() => {
    if(gamesSessions && gameId && user) {
      const currentGameSession = gamesSessions.find(session => session.user === user?.id && session.game === gameId && !session.completed);
      
      if (currentGameSession) {
        setCurrentGameSessionAttempt(currentGameSession?.attempt);
      } else {
        const completedGameSession = gamesSessions
          .filter(session => session.user === user?.id && session.game === gameId && session.completed)
          .reduce((maxSession, session) => (session.id > (maxSession?.id || 0) ? session : maxSession), null);
  
          setCurrentGameSessionAttempt(completedGameSession?.attempt);
      }
    }
  }, [gamesSessions, gameId, user]);

  const { data: scenarioLevels } = useQuery(scenarioLevelsQuery({scenarioId}));
  const { data: scoresStats } = useQuery(scoresStatsQuery({ gameId, attempt: currentGameSessionAttempt }));

  useEffect(() => {
    if(scoresStats && scenarioLevels?.length) {
      // Each level's points sit in exactly one of user_points (decision-
      // scored levels) or user_level_points (level-scored levels, e.g.
      // classified-multistep). The other is null. Summing both with a 0
      // fallback gives a single cross-level total regardless of which
      // scoring mode each level uses.
      const totalPoints = (scoresStats.levels || []).reduce(
        (acc, scoresStat) =>
          acc + (scoresStat.user_points || 0) + (scoresStat.user_level_points || 0),
        0
      );
      setScore(totalPoints);

      // Game-level rank is computed server-side and exposed as
      // session.rank_session — no need to re-derive the tier on the
      // frontend from totalPoints / totalQuestions arithmetic.
      setBadge(scoresStats.session?.rank_session);
    }
  }, [scoresStats, scenarioLevels]);

  useEffect(() => {
    if(badge) {
      if (soundStatus === 'enabled') {
        let soundEffect;
        switch (badge) {
          case 'Master':
            soundEffect = masterEffect.current;
            break;
          case 'Warrior':
            soundEffect = warriorEffect.current;
            break;
          case 'Newbie':
            soundEffect = newbieEffect.current;
            break;
          case 'Noob':
            soundEffect = noobEffect.current;
            break;
          default:
            return;
        }
  
        currentSoundEffect.current = soundEffect;
        currentSoundEffect.current.volume = 0.3;
        soundEffect.play().catch(e => console.error('Error playing sound:', e));
      } else {
        if (currentSoundEffect.current) {
          currentSoundEffect.current.pause();
        }
      }

      return () => {
        if (currentSoundEffect.current) {
          currentSoundEffect.current.pause();
          currentSoundEffect.current.currentTime = 0;
        }
      };
    }
  }, [badge, soundStatus]);

  const badgeImages = {
    Master: BadgeMaster,
    Warrior: BadgeWarrior,
    Newbie: BadgeNewbie,
    Noob: BadgeNoob,
  };

  const badgeDescriptions = {
    Master: 'Outstanding! You’ve earned the Accounting Master badge!',
    Warrior: 'Wonderful! You’ve earned the Accounting Warrior badge!',
    Newbie: 'Not Bad! You’ve earned the Accounting Newbie badge!',
    Noob: 'You’ve earned the Accounting Noob badge!'
  };

  useEffect(() => {
    if (score > 0 && scenarioLevels) {
      const fadeOutTimer = setTimeout(() => {
        if (currentSoundEffect.current) {
          fadeOutSound(currentSoundEffect.current, 2000); // Fade out over 2 seconds
        }
      }, 8000);
      
      const navigateTimer = setTimeout(() => {
        navigate('/classification/summary');
      }, 10000);

      return () => {
        clearTimeout(fadeOutTimer);
        clearTimeout(navigateTimer);
      };
    }
  }, [score, navigate, scenarioLevels, shouldGuidedPagesBeDisplayed]);

  return (
    <Layout isHeaderVisible={true}>
      <div className="page-content badge-page-content">
        <h1 className="badge-page-title">Level {scenarioLevels?.length}</h1>
        {
          score > 0 &&
            <>
              <p className="badge-page-description">{badgeDescriptions[badge]}</p>
              <div className="badge-container">
                <div className="badge-info">
                  <h3 className="badge-game-type">Account Classification</h3>
                  <h2 className="badge-rank">{badge}</h2>
                </div>
                <img className="badge-image" src={badgeImages[badge]} alt={`${badge} Badge`} />
              </div>
            </>
        }
      </div>
    </Layout>
  );
}